import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail } from 'lucide-react';
import { api } from '../services/api';
import { CrmManager } from '../components/CrmManager';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
  padding: 20px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  font-size: 14px;
  transition: all 0.2s ease;

  &:hover {
    background: #f8f9fa;
    color: #495057;
  }
`;

const InfoCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 28px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 6px 16px rgba(15, 23, 42, 0.06);
  max-width: 720px;
`;

const InfoTitle = styled.h2`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 1.5rem;
  color: #0f172a;
  margin: 0 0 0.75rem 0;
`;

const InfoText = styled.p`
  color: #64748b;
  margin: 0 0 1.25rem 0;
  line-height: 1.6;
`;

const PrimaryButton = styled.button`
  background: linear-gradient(135deg, #00d4aa 0%, #00b894 100%);
  color: #0a0e17;
  border: none;
  padding: 0.7rem 1.1rem;
  border-radius: 10px;
  font-weight: 700;
  cursor: pointer;
`;

export const CrmPage: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasCrm, setHasCrm] = useState(false);
  const [planName, setPlanName] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    const loadPlan = async () => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const response = await api.get('/dashboard/plan-usage');
        if (response.data?.success) {
          const plan = response.data?.data?.plan;
          setPlanName(plan?.name || null);
          setHasCrm(!!plan?.has_crm);
        } else {
          setLoadError(response.data?.message || 'Nao foi possivel carregar o plano.');
        }
      } catch (error: any) {
        setLoadError(error?.response?.data?.message || 'Erro ao carregar configuracoes do CRM.');
      } finally {
        setIsLoading(false);
      }
    };

    loadPlan();
  }, []);

  if (isLoading) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} />
            Voltar ao dashboard
          </BackButton>
        </Header>
        <InfoCard>
          <InfoTitle>
            <Mail size={22} />
            Carregando CRM...
          </InfoTitle>
          <InfoText>Estamos preparando os dados do seu plano.</InfoText>
        </InfoCard>
      </PageContainer>
    );
  }

  if (!hasCrm || loadError) {
    return (
      <PageContainer>
        <Header>
          <BackButton onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={18} />
            Voltar ao dashboard
          </BackButton>
        </Header>
        <InfoCard>
          <InfoTitle>
            <Mail size={22} />
            CRM indisponivel
          </InfoTitle>
          <InfoText>
            {loadError
              ? loadError
              : `O modulo de CRM nao esta habilitado no seu plano${planName ? ` (${planName})` : ''}.`}
          </InfoText>
          <PrimaryButton onClick={() => navigate('/billing')}>
            Fazer upgrade do plano
          </PrimaryButton>
        </InfoCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <ArrowLeft size={18} />
          Voltar ao dashboard
        </BackButton>
      </Header>
      <CrmManager apiPrefix="/crm" allowCompanySelection={false} segmentAudience="customers" />
    </PageContainer>
  );
};
